import { createContext, useCallback, useContext, useEffect, useMemo, useState, type AnchorHTMLAttributes, type ReactNode } from "react";

interface LocationState { pathname: string; search: string }
type NavigateOptions = { replace?: boolean; preserveScroll?: boolean };
type Navigate = (to: string, options?: NavigateOptions) => void;

/** /hypotheses/* и /creatives/* — модалки поверх доски: страница не меняется. */
function routeGroup(pathname: string) {
  return pathname.startsWith("/hypotheses/") || pathname.startsWith("/creatives/") ? "/board" : pathname;
}

const RouterContext = createContext<{ location: LocationState; navigate: Navigate } | null>(null);

function readLocation(): LocationState {
  return { pathname: window.location.pathname, search: window.location.search };
}

export function BrowserRouter({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState(readLocation);
  useEffect(() => {
    const update = () => setLocation(readLocation());
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);
  const navigate = useCallback<Navigate>((to, options) => {
    const from = window.location.pathname;
    const next = new URL(to, window.location.origin);
    if (options?.replace) window.history.replaceState(null, "", `${next.pathname}${next.search}${next.hash}`);
    else window.history.pushState(null, "", `${next.pathname}${next.search}${next.hash}`);
    setLocation(readLocation());
    // Скроллим наверх только при реальной смене страницы: открытие модалки
    // поверх текущего экрана не должно терять позицию прокрутки.
    if (!options?.preserveScroll && routeGroup(from) !== routeGroup(next.pathname)) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, []);
  const value = useMemo(() => ({ location, navigate }), [location, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

function useRouter() {
  const value = useContext(RouterContext);
  if (!value) throw new Error("BrowserRouter missing");
  return value;
}

export const useLocation = () => useRouter().location;
export const useNavigate = () => useRouter().navigate;
export const useSearchParams = () => [new URLSearchParams(useLocation().search)] as const;

export function useParams() {
  const { pathname } = useLocation();
  const hypothesis = pathname.match(/^\/hypotheses\/([^/]+)$/);
  const creative = pathname.match(/^\/creatives\/([^/]+)$/);
  return {
    id: hypothesis ? decodeURIComponent(hypothesis[1]!) : undefined,
    creativeId: creative ? decodeURIComponent(creative[1]!) : undefined
  };
}

export function NavLink({ to, className, children, ...props }: Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className"> & {
  to: string;
  className?: string | ((state: { isActive: boolean }) => string);
}) {
  const { location, navigate } = useRouter();
  const isActive = location.pathname === to || (to !== "/" && location.pathname.startsWith(`${to}/`));
  const resolvedClass = typeof className === "function" ? className({ isActive }) : className;
  return <a {...props} href={to} className={resolvedClass} onClick={(event) => {
    props.onClick?.(event);
    if (!event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      event.preventDefault(); navigate(to);
    }
  }}>{children}</a>;
}

export function Navigate({ to, replace = false }: { to: string; replace?: boolean }) {
  const navigate = useNavigate();
  useEffect(() => navigate(to, { replace }), [navigate, replace, to]);
  return null;
}
